import { useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  Input,
  formatPct,
} from '@dataroom/shared';
import type { Room, RoomSeller } from '@dataroom/shared';
import { useRemoveSeller, useRoomSellers, useSetSellerOwnership } from '@/api/hooks';
import { plainError } from '@/lib/errors';
import {
  OWNERSHIP_TOTAL_PROBLEM,
  ownershipLowWarning,
  parseOwnershipPct,
  pctToMilli,
} from '@/lib/ownership';
import { InviteSellerDialog } from './InviteSellerDialog';

function sellerLabel(s: RoomSeller): string {
  return s.displayName && s.email ? `${s.displayName} (${s.email})` : (s.email ?? 'Invited');
}

/**
 * The room's OWNERS (the sellers): every business owner who sends documents, each
 * with the share of the company they own. Together they decide letters of intent
 * and exclusivity by an ownership-weighted majority of the whole company. Owners can
 * be added, re-weighted and removed at any time (even mid-vote — open votes are
 * re-counted); the percentages may not add up to more than 100%, and while the
 * owners hold 50% or less together a plain warning says nothing can be approved. The
 * admin edits every percentage inline (saved as one set), removes an owner, or adds
 * another. Hidden on a room that is no longer active (the server refuses a change
 * there).
 */
export function RoomSellerCard({ room }: { room: Room }) {
  const sellers = useRoomSellers(room.id);
  const save = useSetSellerOwnership(room.id);
  const remove = useRemoveSeller(room.id);
  const [open, setOpen] = useState(false);
  // What the admin has typed, per seller, over what the server holds.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<string | null>(null);
  const active = room.lifecycleState === 'active' || room.lifecycleState === 'draft';

  const list = sellers.data?.sellers ?? [];
  const valueOf = (s: RoomSeller) => edits[s.actorId] ?? String(s.ownershipPct);
  const parsed = list.map((s) => ({ seller: s, ...parseOwnershipPct(valueOf(s)) }));
  const allValid = parsed.every((p) => p.pct !== null);
  const totalMilli = parsed.reduce((sum, p) => sum + (p.pct !== null ? pctToMilli(p.pct) : 0), 0);
  const total = totalMilli / 1000;
  const totalOk = allValid && totalMilli <= 100_000;
  const lowWarning = allValid && totalOk ? ownershipLowWarning(total) : null;
  const dirty = list.some(
    (s) => edits[s.actorId] !== undefined && edits[s.actorId] !== String(s.ownershipPct),
  );
  const canSave = active && dirty && totalOk && !save.isPending;
  const actionError = plainError(save.error) ?? plainError(remove.error);

  const doSave = () => {
    if (!canSave) return;
    remove.reset();
    save.mutate(
      {
        sellers: parsed.map((p) => ({ actorId: p.seller.actorId, ownershipPct: p.pct as number })),
      },
      { onSuccess: () => setEdits({}) },
    );
  };

  const doRemove = (actorId: string) => {
    save.reset();
    remove.mutate(actorId, {
      onSettled: () => setConfirming(null),
      onSuccess: () =>
        setEdits((prev) => {
          const next = { ...prev };
          delete next[actorId];
          return next;
        }),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Owners <HelpTip id="admin.access.owners" className="ml-1" />
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          The business owners who send the documents, and the share of the company each owns. They
          sign in to the seller portal with their email address. Owners together decide letters of
          intent and exclusivity: whatever is entered is approved once owners holding more than 50%
          of the company approve it. You can add owners at any time; the percentages cannot add up
          to more than 100%. The owners are separate from the seller&apos;s team, who never decide
          anything binding.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {sellers.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : sellers.error ? (
          <p className="text-sm text-destructive">{plainError(sellers.error)}</p>
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="room-seller">
            No owner invited yet. Nothing can be approved until owners holding more than 50% of the
            company are added.
          </p>
        ) : (
          <div className="space-y-3" data-testid="room-seller">
            <ul className="divide-y rounded-md border" aria-label="Owners">
              {parsed.map(({ seller, problem }) => (
                <li key={seller.actorId} className="space-y-1 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="break-all text-sm font-medium">{sellerLabel(seller)}</p>
                      {seller.isPrimary ? (
                        <p className="text-xs text-muted-foreground">Asks and answers Q&amp;A</p>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {active ? (
                        <>
                          <Input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            aria-label={`Ownership % for ${sellerLabel(seller)}`}
                            aria-invalid={problem ? true : undefined}
                            value={valueOf(seller)}
                            disabled={save.isPending}
                            onChange={(e) => {
                              save.reset();
                              setEdits((prev) => ({ ...prev, [seller.actorId]: e.target.value }));
                            }}
                            className="w-24"
                          />
                          <span className="text-sm text-muted-foreground" aria-hidden="true">
                            %
                          </span>
                          {confirming === seller.actorId ? (
                            <span className="flex items-center gap-2">
                              <span className="text-xs">
                                Remove {seller.email ?? 'this owner'}?
                              </span>
                              <Button
                                size="sm"
                                variant="danger-ghost"
                                disabled={remove.isPending}
                                onClick={() => doRemove(seller.actorId)}
                              >
                                {remove.isPending ? 'Removing…' : 'Confirm remove'}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>
                                Keep
                              </Button>
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                remove.reset();
                                setConfirming(seller.actorId);
                              }}
                            >
                              Remove
                            </Button>
                          )}
                        </>
                      ) : (
                        <span className="text-sm font-medium">
                          {formatPct(seller.ownershipPct)}
                        </span>
                      )}
                    </div>
                  </div>
                  {problem && edits[seller.actorId] !== undefined ? (
                    <p role="alert" className="text-xs text-destructive">
                      {problem}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-1.5">
              <p
                data-testid="seller-total"
                className={totalOk ? 'text-sm font-medium' : 'text-sm font-medium text-destructive'}
                aria-live="polite"
              >
                {allValid
                  ? totalOk
                    ? `Total ${formatPct(total)}`
                    : `Total ${formatPct(total)} — cannot be more than 100%`
                  : 'Total — fix the percentages above'}
              </p>
              <HelpTip id="admin.access.ownership-total" />
            </div>
            {lowWarning ? (
              <p role="alert" data-testid="owners-low-warning" className="text-sm text-destructive">
                {lowWarning}
              </p>
            ) : null}
            {active && dirty && allValid && !totalOk ? (
              <p className="text-xs text-destructive">{OWNERSHIP_TOTAL_PROBLEM}</p>
            ) : null}
            {active ? (
              <div>
                <Button size="sm" onClick={doSave} disabled={!canSave}>
                  {save.isPending ? 'Saving…' : 'Save percentages'}
                </Button>
              </div>
            ) : null}
          </div>
        )}
        {actionError ? (
          <p role="alert" className="text-sm text-destructive">
            {actionError}
          </p>
        ) : null}
        {active ? (
          <div className="space-y-1">
            <Button
              size="sm"
              variant={list.length > 0 ? 'outline' : 'default'}
              onClick={() => setOpen(true)}
            >
              {list.length > 0 ? 'Add an owner' : 'Invite an owner'}
            </Button>
            {list.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Enter an email already on the list to send their invitation again (if they have not
                signed in yet) or to change their percentage.
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
      {open ? (
        <InviteSellerDialog roomId={room.id} setup={false} onClose={() => setOpen(false)} />
      ) : null}
    </Card>
  );
}
